import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { Transition, TransitionType } from "../../types";
import type {
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";
import { findClip } from "./clip-helpers";

const ok = (): ValidationResult => ({ valid: true, errors: [] });
const err = (message: string, path?: string): ValidationResult => ({
  valid: false,
  errors: [{ code: "INVALID_PARAMS", message, path }],
});

function findTransition(
  project: Project,
  transitionId: string,
): Transition | null {
  for (const track of project.timeline.tracks) {
    const transition = track.transitions?.find((t) => t.id === transitionId);
    if (transition) return transition;
  }
  return null;
}

// Two transitions hold the same slot when they join the same pair of clips or,
// for a one-sided transition, when they sit on the same edge of one clip. A set
// replaces the holder of the slot rather than stacking a second transition on
// top of it, which is why this is not an id comparison.
function isSameTransitionPlacement(
  candidate: Transition,
  transition: Transition,
): boolean {
  if (candidate.id === transition.id) {
    return true;
  }

  if (candidate.clipBId || transition.clipBId) {
    return (
      candidate.clipAId === transition.clipAId &&
      candidate.clipBId === transition.clipBId
    );
  }

  return (
    candidate.clipAId === transition.clipAId &&
    (candidate.edge ?? "out") === (transition.edge ?? "out")
  );
}

function requireTransitionId(transitionId: unknown): ValidationError[] {
  if (!transitionId || typeof transitionId !== "string") {
    return [
      {
        code: "INVALID_PARAMS",
        message: "Transition ID is required and must be a string",
        path: "params.transitionId",
      },
    ];
  }
  return [];
}

const transitionAdd: ActionHandler = {
  type: "transition/add",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      clipAId?: unknown;
      clipBId?: unknown;
      duration?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!params.clipAId || typeof params.clipAId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Clip A ID is required and must be a string",
        path: "params.clipAId",
      });
    }

    if (!params.clipBId || typeof params.clipBId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Clip B ID is required and must be a string",
        path: "params.clipBId",
      });
    }

    if (typeof params.duration !== "number" || params.duration <= 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Duration must be a positive number",
        path: "params.duration",
      });
    }

    if (params.clipAId && params.clipBId) {
      const clipA = findClip(project, params.clipAId as string);
      const clipB = findClip(project, params.clipBId as string);

      if (!clipA) {
        errors.push({
          code: "CLIP_NOT_FOUND",
          message: `Clip A with ID ${params.clipAId} not found`,
          path: "params.clipAId",
        });
      }

      if (!clipB) {
        errors.push({
          code: "CLIP_NOT_FOUND",
          message: `Clip B with ID ${params.clipBId} not found`,
          path: "params.clipBId",
        });
      }

      if (clipA && clipB) {
        if (clipA.trackId !== clipB.trackId) {
          errors.push({
            code: "INVALID_PARAMS",
            message: "Clips must be on the same track for transition",
            path: "params",
          });
        }
        const clipAEnd = clipA.startTime + clipA.duration;
        const clipBEnd = clipB.startTime + clipB.duration;
        const tolerance = 0.001;

        const aBeforeB = Math.abs(clipAEnd - clipB.startTime) < tolerance;
        const bBeforeA = Math.abs(clipBEnd - clipA.startTime) < tolerance;

        if (!aBeforeB && !bBeforeA) {
          errors.push({
            code: "INVALID_PARAMS",
            message: "Clips must be adjacent for transition",
            path: "params",
          });
        }
      }
    }

    return { valid: errors.length === 0, errors };
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const timeline = project.timeline as MutableTimeline;
    const params = action.params as {
      clipAId: string;
      clipBId: string;
      transitionType: TransitionType;
      duration: number;
    };
    const clipA = findClip(project, params.clipAId);
    if (clipA) {
      const track = timeline.tracks.find(
        (t: MutableTrack) => t.id === clipA.trackId,
      );
      if (track) {
        const newTransition: Transition = {
          id:
            (params as { transitionId?: string }).transitionId ??
            `transition-${crypto.randomUUID()}`,
          clipAId: params.clipAId,
          clipBId: params.clipBId,
          type: params.transitionType,
          duration: params.duration,
          params: {},
        };
        // Redo replays this same action object; without this it would mint a
        // fresh id and the stored inverse would target the one from the first
        // run.
        (action.params as Record<string, unknown>).transitionId =
          newTransition.id;
        track.transitions = [...(track.transitions || []), newTransition];
        ctx.lastAddedIds.set("transition", newTransition.id);
      }
    }
  },

  invert(action: Action): Action | null {
    return createInverseAction(action, "transition/remove", {
      transitionId: "__LAST_ADDED__",
    });
  },
};

const transitionSet: ActionHandler = {
  type: "transition/set",

  validate(action: Action, project: Project): ValidationResult {
    const transition = (
      action.params as { transition?: { id?: unknown; clipAId?: unknown } }
    ).transition;

    if (
      !transition ||
      typeof transition.id !== "string" ||
      typeof transition.clipAId !== "string"
    ) {
      return err(
        "A valid transition with id and clipAId is required",
        "params.transition",
      );
    }

    if (!findClip(project, transition.clipAId)) {
      return {
        valid: false,
        errors: [
          {
            code: "CLIP_NOT_FOUND",
            message: `Clip A with ID ${transition.clipAId} not found`,
            path: "params.transition.clipAId",
          },
        ],
      };
    }

    return ok();
  },

  apply(action: Action, project: Project): void {
    const timeline = project.timeline as MutableTimeline;
    const params = action.params as { transition: Transition };
    const clipA = findClip(project, params.transition.clipAId);
    if (clipA) {
      timeline.tracks = timeline.tracks.map((track: MutableTrack) =>
        track.id === clipA.trackId
          ? {
              ...track,
              transitions: [
                ...(track.transitions || []).filter(
                  (t: Transition) =>
                    !isSameTransitionPlacement(t, params.transition),
                ),
                params.transition,
              ],
            }
          : track,
      );
    }
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { transition: Transition };
    const clipA = findClip(projectBefore, params.transition.clipAId);
    if (!clipA) return null;
    const track = projectBefore.timeline.tracks.find(
      (t) => t.id === clipA.trackId,
    );
    if (!track) return null;

    return createInverseAction(action, "transition/restoreTrack", {
      trackId: track.id,
      transitions: (track.transitions || []).map((t) => ({ ...t })),
    });
  },
};

/**
 * The inverse of a set, carrying the whole transition list a track held before
 * the edit. Only this file mints it, which is why it had a case in the executor
 * and in neither of the other two switches.
 *
 * Its validate and its invert are written here so the three sides agree, not
 * because a live bug needs them: undo applies a stored inverse straight through
 * applyAction, without validating it and without inverting it again, so neither
 * hole is reachable from today's undo or redo path. They exist for the day this
 * type is dispatched like any other.
 *
 * The validate stays structural, a track id and a transitions array, which is
 * the shape check every other handler makes and which nothing this file mints
 * can fail. It does not grow a check that the track is still on the timeline:
 * the legacy switch let that through, and apply already leaves an unknown track
 * id alone.
 */
const transitionRestoreTrack: ActionHandler = {
  type: "transition/restoreTrack",

  validate(action: Action): ValidationResult {
    const params = action.params as {
      trackId?: unknown;
      transitions?: unknown;
    };
    if (typeof params.trackId !== "string" || !params.trackId) {
      return err("Track ID is required and must be a string", "params.trackId");
    }
    return Array.isArray(params.transitions)
      ? ok()
      : err(
          "transition/restoreTrack requires a transitions array",
          "params.transitions",
        );
  },

  apply(action: Action, project: Project): void {
    const timeline = project.timeline as MutableTimeline;
    const params = action.params as {
      trackId: string;
      transitions: Transition[];
    };
    timeline.tracks = timeline.tracks.map((track: MutableTrack) =>
      track.id === params.trackId
        ? { ...track, transitions: params.transitions }
        : track,
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { trackId: string };
    const track = projectBefore.timeline.tracks.find(
      (t) => t.id === params.trackId,
    );
    if (!track) return null;

    return createInverseAction(action, "transition/restoreTrack", {
      trackId: params.trackId,
      transitions: (track.transitions || []).map((t) => ({ ...t })),
    });
  },
};

const transitionRemove: ActionHandler = {
  type: "transition/remove",

  validate(action: Action): ValidationResult {
    const errors = requireTransitionId(
      (action.params as { transitionId?: unknown }).transitionId,
    );
    return { valid: errors.length === 0, errors };
  },

  apply(action: Action, project: Project): void {
    const timeline = project.timeline as MutableTimeline;
    const params = action.params as { transitionId: string };
    timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
      ...track,
      transitions: (track.transitions || []).filter(
        (t: Transition) => t.id !== params.transitionId,
      ),
    }));
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { transitionId: string };
    const transition = findTransition(projectBefore, params.transitionId);
    if (!transition) return null;

    return createInverseAction(action, "transition/restore", {
      transition: { ...transition },
    });
  },
};

/**
 * The inverse of a remove, minted only by this file, and missing from the same
 * two switches as restoreTrack for the same reason. See the note there for why
 * the pieces added here are guards rather than fixes.
 *
 * The validate is deliberately structural only. Mirroring the set validate and
 * rejecting a transition whose clip A has since been deleted would be a
 * rejection the legacy switch never made, and it would buy nothing: apply
 * already no-ops on that shape, and the undo path never asks.
 */
const transitionRestore: ActionHandler = {
  type: "transition/restore",

  validate(action: Action): ValidationResult {
    const transition = (
      action.params as { transition?: { id?: unknown; clipAId?: unknown } }
    ).transition;

    return transition &&
      typeof transition.id === "string" &&
      typeof transition.clipAId === "string"
      ? ok()
      : err(
          "A valid transition with id and clipAId is required",
          "params.transition",
        );
  },

  apply(action: Action, project: Project): void {
    const timeline = project.timeline as MutableTimeline;
    const params = action.params as { transition: Transition };
    const clipA = findClip(project, params.transition.clipAId);
    if (clipA) {
      timeline.tracks = timeline.tracks.map((track: MutableTrack) =>
        track.id === clipA.trackId
          ? {
              ...track,
              transitions: [...(track.transitions || []), params.transition],
            }
          : track,
      );
    }
  },

  invert(action: Action): Action | null {
    const params = action.params as { transition: Transition };
    return createInverseAction(action, "transition/remove", {
      transitionId: params.transition.id,
    });
  },
};

const transitionUpdate: ActionHandler = {
  type: "transition/update",

  validate(action: Action): ValidationResult {
    const params = action.params as {
      transitionId?: unknown;
      duration?: unknown;
    };
    const errors: ValidationError[] = [
      ...requireTransitionId(params.transitionId),
    ];

    if (params.duration !== undefined) {
      if (typeof params.duration !== "number" || params.duration <= 0) {
        errors.push({
          code: "INVALID_PARAMS",
          message: "Duration must be a positive number",
          path: "params.duration",
        });
      }
    }

    return { valid: errors.length === 0, errors };
  },

  apply(action: Action, project: Project): void {
    const timeline = project.timeline as MutableTimeline;
    const params = action.params as {
      transitionId: string;
      type?: TransitionType;
      duration?: number;
      params?: Record<string, unknown>;
    };
    timeline.tracks = timeline.tracks.map((track: MutableTrack) => ({
      ...track,
      transitions: (track.transitions || []).map((t: Transition) =>
        t.id === params.transitionId
          ? {
              ...t,
              ...(params.type !== undefined && { type: params.type }),
              ...(params.duration !== undefined && {
                duration: params.duration,
              }),
              ...(params.params !== undefined && {
                params: { ...t.params, ...params.params },
              }),
            }
          : t,
      ),
    }));
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { transitionId: string };
    const transition = findTransition(projectBefore, params.transitionId);
    if (!transition) return null;

    return createInverseAction(action, "transition/update", {
      transitionId: params.transitionId,
      type: transition.type,
      duration: transition.duration,
      params: { ...transition.params },
    });
  },
};

for (const handler of [
  transitionAdd,
  transitionSet,
  transitionRestoreTrack,
  transitionRemove,
  transitionRestore,
  transitionUpdate,
]) {
  registerActionHandler(handler);
}
